import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/common/alert-dialog"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Button } from "@components/common/button"
import { Trash2Icon } from "lucide-react"
import useTranslator from "@hooks/use-translator"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@pma/hooks/use-toast"
import { DictType } from "@pma/types"
import { useNavigate, useSearchParams } from "react-router-dom"
import axios from "axios"
import { DELETE_NOTE_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"

function DeleteNote({
  children,
  note,
  notes,
  triggerClass,
  defaultOpen = false,
  onClose = () => {},
  fromExpandCalling = false,
  setSelectedNotes,
}: any) {
  const { translate } = useTranslator()
  const navigate = useNavigate()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const deleteNote = async () => {
    const promiseArr: Array<any> = []

    if (notes) {
      notes.forEach((val: any) => {
        if (val.checked) {
          const promise = axios.delete(
            SERVER_URL + DELETE_NOTE_ENDPOINT + `/${val.id}`,

            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            },
          )
          promiseArr.push(promise)
        }
      })

      return Promise.all(promiseArr)
        .then((values) => {
          if (values[0].status === 200) {
            if (fromExpandCalling) {
              queryClient.invalidateQueries({ queryKey: ["get-notes-for-expand"] })
            } else {
              newParams.set("revalidate", "tasks")
              navigate(`?${newParams.toString()}`)
              // queryClient.invalidateQueries({ queryKey: ['get-tasks'] })
              queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
            }
            toast({
              title: translate("Success"),
              description: translate("Success"),
              variant: "success",
            })
          }
          return values
        })
        .catch((error) => {
          const errorMessage = error.message
          toast({
            title: translate("Error"),
            description: errorMessage || translate("An error occurred"),
            variant: "error",
          })
          return errorMessage
        })
    } else {
      const response = await axios.delete(SERVER_URL + DELETE_NOTE_ENDPOINT + `/${note?.id}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      return response.data
    }
  }

  const deleteNoteMutation = useMutation({
    mutationFn: deleteNote,
    onSuccess: () => {
      if (fromExpandCalling) {
        queryClient.invalidateQueries({ queryKey: ["get-notes-for-expand"] })
        queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
        queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })
      } else {
        queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
        queryClient.invalidateQueries({ queryKey: ["get-notes-for-expand"] })
        queryClient.invalidateQueries({ queryKey: ["grouped-tasks"] })

        if (setSelectedNotes) setSelectedNotes([])
        newParams.set("revalidate", "tasks")
        navigate(`?${newParams.toString()}`)
      }
      toast({
        title: translate("Success"),
        description: translate("Note deleted successfully"),
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: errorMessage || translate("An error occurred"),
        variant: "error",
      })
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
    },
  })

  return (
    <AlertDialog defaultOpen={defaultOpen}>
      <AlertDialogTrigger className={triggerClass}>
        <Tooltip>
          <TooltipTrigger className={triggerClass}>
            {children
              ? children
              : !defaultOpen && (
                  <Button
                    variant={"ghost"}
                    className='flex h-6 w-6 flex-col items-center justify-center rounded-full text-red-400 hover:bg-slate-300'
                    size={"icon"}
                  >
                    <Trash2Icon size={14} />
                  </Button>
                )}
          </TooltipTrigger>
          <TooltipContent className='flex items-center gap-1 bg-destructive'>
            <h4>
              {translate(`Delete`)} {note && translate("Note")} {notes && translate("Notes")}
            </h4>
          </TooltipContent>
        </Tooltip>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{translate("Proceed to delete")}</AlertDialogTitle>
          <AlertDialogDescription>
            {translate("This action cannot be undone")}.{" "}
            {translate("This will permanently delete the ")}
            {note && <span className='text-red-600'>{translate("Note")}</span>}
            {notes && <span className='text-red-600'>{translate("Selected notes")}</span>}?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onClose()}>{translate("Cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className='bg-destructive'
            disabled={deleteNoteMutation?.isPending}
            onClick={() => deleteNoteMutation.mutate()}
          >
            {translate("Continue")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default DeleteNote
